import { createContext, useContext } from "react"

import { Allergen } from "../../../admin/products/productsTypes"

import { WeekMenuActions, WeekStore } from "./useWeekMenuStore"
import { AiDaySettings } from "./weekMenuAiBank"
import { RecentDish, WeekMenuDragSource } from "./weekMenuTypes"

export type { RecentDish } from "./weekMenuTypes"

// The context value stays identity stable so memoized cells re-render only on their own data.
type WeekMenuContextValue = Readonly<{
    actions: WeekMenuActions
    allergenOptions: ReadonlyArray<Allergen>
    getWeek: () => WeekStore
    getRecentDishes: () => ReadonlyArray<RecentDish>
    /** The AI settings last used for a day, so per-cell regeneration keeps the day's theme. */
    getAiDaySettings: (dayIndex: number) => AiDaySettings
    /** Localized full weekday names of the columns. */
    dayLabels: ReadonlyArray<string>
    /** True while the AI banner is open, when fresh cells highlight and offer regeneration. */
    isAiReviewActive: boolean
    /** Set while a cell is dragged, so the other cells of its row can highlight as drop targets. */
    dragSource: WeekMenuDragSource | null
    onCellDragStart: (source: WeekMenuDragSource) => void
    onCellDragEnd: () => void
}>

const WeekMenuContext = createContext<WeekMenuContextValue | null>(null)

export function useWeekMenu(): WeekMenuContextValue {
    const context = useContext(WeekMenuContext)
    if (!context) {
        throw new Error("useWeekMenu must be used within a WeekMenuProvider")
    }
    return context
}

export const WeekMenuProvider = WeekMenuContext.Provider
